import React, { useState, useEffect, ReactNode } from 'react';
import ReactDOMServer from 'react-dom/server';

interface IFrameProps extends React.IframeHTMLAttributes<HTMLIFrameElement> {
  children: ReactNode;
  styleSelector: string;
}

export const IFrame: React.FC<IFrameProps> = ({ children, styleSelector, ...props }) => {
  const [contentRef, setContentRef] = useState<HTMLIFrameElement | null>(null);
  const [iframeSrcDoc, setIframeSrcDoc] = useState<string>();

  useEffect(() => {
    if (contentRef) {
      const win = contentRef?.contentWindow;

      if (!win) {
        return;
      }

      const htmlString = ReactDOMServer.renderToStaticMarkup(children);

      const linkEls = win.parent.document.querySelectorAll(styleSelector);

      const htmlChildren: string[] = [];

      if (linkEls.length) {
        linkEls.forEach((el) => {
          htmlChildren.push(el.outerHTML);
        });
      }

      htmlChildren.push(htmlString);

      const onLoadFrame = () => {
        (win.document.body as any).className = 'antd-scope-wrap';
        (win.document.body as any).style = 'background-color: unset; width: unset; height: unset; word-wrap: break-word;';

        copyStylesToIframe(contentRef);

        resizeIframe(contentRef);
      };

      if ((contentRef as any).attachEvent) {
        (contentRef as any).attachEvent('onload', onLoadFrame);
      } else {
        contentRef.onload = onLoadFrame;
      }

      setIframeSrcDoc(htmlChildren.join(''));
    }
  }, [contentRef, children, styleSelector]);

  const copyStylesToIframe = (iframe: any) => {
    if (!iframe || !iframe.contentDocument) return;

    const iframeDoc = iframe.contentDocument;
    const iframeHead = iframeDoc.head || iframeDoc.getElementsByTagName('head')[0];
    const iframeBody = iframeDoc.body;

    if (!iframeHead || !iframeBody) return;

    // Copy only Ant Design styles (by class or href containing "antd")
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
      const isAntdStyle =
        node.outerHTML.includes('ant') || // For inline <style> blocks generated dynamically
        (node.getAttribute('href') && node?.getAttribute('href')?.includes('React_Sources')) ||
        (node.getAttribute('href') && node?.getAttribute('href')?.includes('react-sources')); // For <link> external css

      if (isAntdStyle) {
        iframeHead.appendChild(node.cloneNode(true));
      }
    });

    // Copy classes from main .ant-app to iframe body
    const antApp = document.querySelector('.ant-app');
    if (antApp) {
      iframeBody.className = antApp.className;
    }

    iframe.style.visibility = '';
  };

  const resizeIframe = (obj: HTMLIFrameElement) => {
    if (obj.contentDocument?.scrollingElement) {
      obj.style.height = (obj.contentDocument.scrollingElement as any).offsetHeight + 'px';
    }
  };

  return <iframe style={{ visibility: 'hidden' }} {...props} ref={setContentRef} srcDoc={iframeSrcDoc}></iframe>;
};
